import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useOutletContext } from 'react-router-dom';
import { CheckCircle, AlertTriangle, Clock, Users, Plus, X } from 'lucide-react';
import PageHeader from './PageHeader';

import { API } from '../apiClient';
function api(path, options = {}) {
  const token = localStorage.getItem('token');
  return fetch(`${API}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? {Authorization: `Bearer ${token}`} : {}),
      ...(options.headers || {})
    }
  }).then(async r => {
    if (!r.ok) throw new Error((await r.json()).detail || 'API error');
    return r.json();
  });
}

function formatDate(dateStr) {
  const d = new Date(dateStr);
  const today = new Date();
  const tomorrow = new Date(today); tomorrow.setDate(tomorrow.getDate() + 1);
  if (dateStr === today.toISOString().split('T')[0]) return 'Dzisiaj';
  if (dateStr === tomorrow.toISOString().split('T')[0]) return 'Jutro';
  return d.toLocaleDateString('pl-PL', {weekday: 'short', day: 'numeric', month: 'short'});
}

function formatDateTime(value) {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  return d.toLocaleString('pl-PL', {day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'});
}

function formatTime(dateStr) {
  return new Date(dateStr).toLocaleDateString('pl-PL', {day: 'numeric', month: 'short'});
}

function reservationLabel(job, field = 'reservation_number') {
  return job?.[field] ? `rezerwacja ${job[field]}` : 'rezerwacja —';
}

function staffVisibleNote(note) {
  const text = String(note || '').trim();
  if (!text) return '';
  if (text.toLowerCase().startsWith('automatycznie')) return '';
  return text;
}

// ===== CLEANING COMPONENT (with week view + filters) =====
export default function Cleaning({ jobs: initialJobs, reload: parentReload, user }) {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const ctx = useOutletContext() || {};
  const safeUser = user || ctx.user || { role: 'guest', language: 'pl' };
  const safeReload = parentReload || ctx.reload;
  const selectedApartmentId = ctx.selectedApartmentId || null;
  const apartmentFilter = selectedApartmentId ? (item) => Number(item.property_id) === Number(selectedApartmentId) : () => true;
  const [jobs, setJobs] = useState(initialJobs || ctx.jobs || []);
  const [selectedJob, setSelectedJob] = useState(null);
  const [checklist, setChecklist] = useState([]);
  const [filter, setFilter] = useState('action'); // admin: action | upcoming | planned | done | all
  const [adminSearch, setAdminSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [actionError, setActionError] = useState('');
  const [showAllFuture, setShowAllFuture] = useState(false);

  const today = new Date().toISOString().split('T')[0];

  // Group jobs by scheduled_date
  const grouped = {};
  for (const j of jobs) {
    const d = j.scheduled_date;
    if (!grouped[d]) grouped[d] = [];
    grouped[d].push(j);
  }
  const sortedDates = Object.keys(grouped).sort();
  const staffCurrent = safeUser.role === 'staff' ? jobs.find(j => j.is_current_action) : null;

  // Filter dates
  const filteredDates = sortedDates.filter(d => {
    if (filter === 'today') return d === today;
    if (filter === 'week') {
      const t = new Date(today);
      const end = new Date(t); end.setDate(end.getDate() + 7);
      return new Date(d) >= t && new Date(d) <= end;
    }
    return true; // 'all'
  });

  // Load jobs before staff/admin branch renders.
  useEffect(() => {
    const sourceJobs = initialJobs && initialJobs.length > 0 ? initialJobs : (ctx.jobs || []);
    setJobs(selectedApartmentId ? sourceJobs.filter(apartmentFilter) : sourceJobs);
  }, [initialJobs, ctx.jobs, selectedApartmentId]);

  useEffect(() => {
    if (staffCurrent?.status === 'in_progress' && selectedJob?.id !== staffCurrent.id) {
      openJob(staffCurrent);
    }
  }, [safeUser.role, staffCurrent?.id, staffCurrent?.status, selectedJob?.id]);

  // ===== MARIA VIEW =====
  if (safeUser.role === 'staff') {
    const current = staffCurrent;
    const todayStr = new Date().toISOString().split('T')[0];
    const activeOrPlannedJobs = jobs
      .filter(j => j.status !== 'done')
      .sort((a, b) => String(a.scheduled_date).localeCompare(String(b.scheduled_date)) || Number(a.id || 0) - Number(b.id || 0));
    const upcomingJobs = activeOrPlannedJobs.filter(j => String(j.scheduled_date || '') >= todayStr);
    const nextJob = current || upcomingJobs[0] || activeOrPlannedJobs[0] || null;
    const future = upcomingJobs
      .filter(j => !j.is_current_action && j.id !== nextJob?.id)
      .sort((a, b) => String(a.scheduled_date).localeCompare(String(b.scheduled_date)) || Number(a.id || 0) - Number(b.id || 0));
    const displayedFuture = showAllFuture ? future : future.slice(0, 1);
    const hiddenFutureCount = Math.max(0, future.length - displayedFuture.length);
    const recentDone = jobs
      .filter(j => j.status === 'done')
      .sort((a, b) => String(b.scheduled_date).localeCompare(String(a.scheduled_date)))
      .slice(0, 3);

    async function ack(jobId) {
      setActionError('');
      try {
        await api(`/cleaning-jobs/${jobId}/acknowledge`, { method: 'PATCH' });
        window.dispatchEvent(new CustomEvent('hermes:christ-egg', { detail: { seed: 'ack:' + jobId } }));
        await safeReload?.();
        await loadJobs();
      } catch (err) {
        setActionError(err.message || 'Nie udało się potwierdzić');
      }
    }

    const mainJob = current || nextJob;
    const hasCurrentAction = Boolean(current);

    return (
      <section className="maria-simple-page">
        <PageHeader
          icon="🧹"
          title="Sprzątanie"
          helpKey="cleaning-staff"
        />
        {actionError && <div className="error">{actionError}</div>}

        <article className={`card maria-now-card ${hasCurrentAction ? 'is-current' : 'is-next'}`}>
          <div className="maria-now-head">
            <span className={`status-chip maria-occupancy-chip ${mainJob?.occupancy_label?.startsWith('Goście') ? 'occupied' : 'empty'}`}>
              {mainJob?.occupancy_label || 'Mieszkanie'}
            </span>
            {mainJob?.availability_label && <span className="maria-mini-note">{mainJob.availability_label}</span>}
          </div>

          {mainJob ? (
            <>
              <p className="maria-eyebrow">{hasCurrentAction ? 'Do zrobienia teraz' : 'Najbliższy termin'}</p>
              <h2>{hasCurrentAction ? (mainJob.status === 'in_progress' ? 'Sprzątasz teraz' : 'Możesz sprzątać') : formatDate(mainJob.scheduled_date)}</h2>

              <div className="maria-key-info">
                <div>
                  <small>Wymeldowanie</small>
                  <strong>{mainJob.previous_checkout_label || mainJob.checkout || mainJob.scheduled_date}</strong>
                </div>
                <div>
                  <small>Przyjazd</small>
                  <strong>{mainJob.checkin_time || '15:00'}</strong>
                </div>
              </div>

              {mainJob.deadline_label && <p className="maria-deadline">⏰ {mainJob.deadline_label}</p>}
              {mainJob.alarm_label && <p className="maria-deadline alarm">⚠️ {mainJob.alarm_label}</p>}

              <div className="maria-prep-box maria-prep-big">
                <strong>{mainJob.prepare_guest_count || 0} {(mainJob.prepare_guest_count || 0) === 1 ? 'osoba' : ((mainJob.prepare_guest_count || 0) >= 2 && (mainJob.prepare_guest_count || 0) <= 4 ? 'osoby' : 'osób')}</strong>
                <span>{mainJob.prepare_label || `Pościel: ${mainJob.prepare_linen_count || 0}, ręczniki: ${mainJob.prepare_towel_count || 0}`}</span>
                {staffVisibleNote(mainJob.prepare_guest_notes) && <small>{staffVisibleNote(mainJob.prepare_guest_notes)}</small>}
              </div>

              {hasCurrentAction ? (
                <>
                  {mainJob.status === 'planned' && (
                    <button className="primary maria-big-action" onClick={() => startJob(mainJob)}>Rozpocznij sprzątanie</button>
                  )}
                  {mainJob.status === 'in_progress' && (
                    <button className="primary maria-big-action" onClick={() => markDone(mainJob)}>Zakończ sprzątanie</button>
                  )}
                </>
              ) : mainJob.acknowledged_by_staff ? (
                <span className="ack-badge">✓ Widziane</span>
              ) : (
                <button className="secondary maria-big-action" onClick={() => ack(mainJob.id)}>Widziałam</button>
              )}
            </>
          ) : (
            <>
              <p className="maria-eyebrow">Teraz</p>
              <h2>Brak sprzątania</h2>
              <p className="muted" style={{marginTop: '6px'}}>Gdy pojawi się nowe zadanie, będzie tutaj.</p>
            </>
          )}
        </article>

        {selectedJob && current && selectedJob.id === current.id && (
          <article className="card maria-job-detail">
            <h3>Checklista</h3>
            <p className="muted" style={{marginTop: '-4px'}}>{reservationLabel(selectedJob)}</p>
            {current.prepare_guest_count > 0 && (
              <p className="maria-prep-box">
                <strong>{current.prepare_guest_count} osób</strong>
                <span>{current.prepare_label}</span>
                {staffVisibleNote(current.prepare_guest_notes) && <small>{staffVisibleNote(current.prepare_guest_notes)}</small>}
              </p>
            )}
            <ul className="checklist maria-clean-checklist">
              {checklist.map(item => (
                <li key={item.id} className={item.completed ? 'done' : ''}>
                  <label>
                    <input
                      type="checkbox"
                      checked={!!item.completed}
                      onChange={() => toggle(item)}
                    />
                    <span>{safeUser.language === 'uk' ? item.label_uk : item.label_pl}</span>
                  </label>
                </li>
              ))}
            </ul>
            {current.status !== 'done' && (
              <button className="primary maria-big-action" onClick={() => markDone(current)}>Zakończ sprzątanie</button>
            )}
            <IssueSection jobId={selectedJob.id} user={safeUser} />
          </article>
        )}

        {future.length > 0 && (
          <article className="card maria-future-card">
            <div className="maria-section-head">
              <div>
                <h3>{showAllFuture ? 'Plan sprzątań' : 'Później'}</h3>
                {!showAllFuture && hiddenFutureCount > 0 && <small className="muted">Pokazuję tylko najbliższe kolejne zadanie.</small>}
              </div>
            </div>
            {displayedFuture.map(j => (
              <div className="maria-future-row" key={j.id}>
                <div>
                  <strong>{formatDate(j.scheduled_date)}</strong>
                  <span>Przygotować dla {j.prepare_guest_count || 0} osób</span>
                  {j.previous_checkout_label && <small className="muted">Wymeldowanie: {j.previous_checkout_label}</small>}
                  {j.availability_label && <small className="muted">{j.availability_label}</small>}
                </div>
                <div className="maria-future-actions">
                  {j.acknowledged_by_staff ? <span className="ack-badge">✓ Widziane</span> : <button className="secondary small" onClick={() => ack(j.id)}>Widziałam</button>}
                </div>
              </div>
            ))}
            {future.length > 1 && (
              <button type="button" className="secondary maria-show-more" onClick={() => setShowAllFuture(v => !v)}>
                {showAllFuture ? 'Pokaż mniej' : `Pokaż więcej (${hiddenFutureCount})`}
              </button>
            )}
          </article>
        )}

        {recentDone.length > 0 && (
          <article className="card maria-history-card">
            <h3>Zrobione ostatnio</h3>
            {recentDone.map(j => (
              <div className="maria-history-row" key={j.id}>✓ {formatDate(j.scheduled_date)}</div>
            ))}
          </article>
        )}
      </section>
    );
  }

  async function loadJobs() {
    try {
      setLoading(true);
      const jobsData = await api(selectedApartmentId ? `/cleaning-jobs?property_id=${encodeURIComponent(selectedApartmentId)}` : '/cleaning-jobs');
      setJobs(jobsData);
    } catch (err) {
      console.error('Failed to load jobs:', err);
    } finally {
      setLoading(false);
    }
  }

  async function load() {
    if (!localStorage.getItem('token')) return;
    try {
      setLoading(true);
      const [j] = await Promise.all([
        api(selectedApartmentId ? `/cleaning-jobs?property_id=${encodeURIComponent(selectedApartmentId)}` : '/cleaning-jobs')
      ]);
      setJobs(j);
    } catch (err) {
      console.error('Failed to load data:', err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const sourceJobs = initialJobs && initialJobs.length > 0 ? initialJobs : (ctx.jobs || []);
    setJobs(selectedApartmentId ? sourceJobs.filter(apartmentFilter) : sourceJobs);
  }, [initialJobs, ctx.jobs, selectedApartmentId]);

  async function openJob(job) {
    setSelectedJob(job);
    try {
      const list = await api(`/cleaning-jobs/${job.id}/checklist`);
      setChecklist(list);
      // Update URL to include job ID
      navigate(`/sprzatanie/${job.id}`);
    } catch (err) {
      console.error('Failed to load checklist:', err);
    }
  }
  
  // Auto-open cleaning job if jobId param is set
  useEffect(() => {
    if (!jobId || jobs.length === 0) return;
    
    const job = jobs.find(j => j.id === parseInt(jobId));
    if (job && !selectedJob) {
      openJob(job);
    }
  }, [jobId, jobs]);

  async function toggle(item) {
    try {
      await api(`/checklist/${item.id}?completed=${item.completed ? 0 : 1}`, {method: 'PATCH'});
      window.dispatchEvent(new CustomEvent('hermes:christ-egg', { detail: { seed: 'checklist:' + item.id } }));
      const list = await api(`/cleaning-jobs/${selectedJob.id}/checklist`);
      setChecklist(list);
      await safeReload?.();
    } catch (err) {
      console.error('Failed to load checklist:', err);
    }
  }

  async function markDone(job) {
    try {
      await api(`/cleaning-jobs/${job.id}`, {
        method: 'PATCH',
        body: JSON.stringify({status: 'done'})
      });
      await safeReload?.();
      await loadJobs(); // Reload jobs to reflect status change
      if (selectedJob?.id === job.id) {
        setSelectedJob(null);
        setChecklist([]);
        // Remove job ID from URL
        navigate('/sprzatanie');
      }
    } catch (err) {
      console.error('Failed to mark job as done:', err);
    }
  }

  async function startJob(job) {
    try {
      await api(`/cleaning-jobs/${job.id}`, {
        method: 'PATCH',
        body: JSON.stringify({status: 'in_progress'})
      });
      await safeReload?.();
      await loadJobs(); // Reload jobs to reflect status change
      await openJob({...job, status: 'in_progress'});
    } catch (err) {
      console.error('Failed to start job:', err);
    }
  }

  async function confirmPayment(job) {
    if (job.status !== 'done') return;
    const ok = window.confirm(
      `Potwierdzić zapłatę ${job.payment_amount || 100} PLN dla Marii za to sprzątanie?\n\n` +
      `Po potwierdzeniu status płatności zmieni się na „Zapłacone" i zostanie zapisane Twoje imię oraz godzina.`
    );
    if (!ok) return;
    try {
      const updated = await api(`/cleaning-jobs/${job.id}/payment`, {
        method: 'PATCH',
        body: JSON.stringify({payment_status: 'paid'})
      });
      await safeReload?.();
      await loadJobs();
      if (selectedJob?.id === job.id) {
        setSelectedJob(prev => ({
          ...prev,
          payment_status: updated.payment_status || 'paid',
          payment_paid_at: updated.payment_paid_at || '',
          payment_paid_by_name: updated.payment_paid_by_name || (safeUser?.name || ''),
          payment_amount: updated.payment_amount || prev.payment_amount
        }));
      }
      const toast = document.createElement('div');
      toast.innerText = '✓ Zapłacone — zapisano w historii';
      toast.className = 'toast';
      document.body.appendChild(toast);
      setTimeout(() => document.body.removeChild(toast), 2500);
    } catch (err) {
      console.error('Failed to confirm payment:', err);
      const toast = document.createElement('div');
      toast.innerText = 'Błąd: nie udało się oznaczyć zapłaty. Sprawdź logi serwera.';
      toast.className = 'toast error';
      document.body.appendChild(toast);
      setTimeout(() => document.body.removeChild(toast), 3500);
    }
  }

  async function updateJobField(jobId, field, value) {
    try {
      const payload = {};
      payload[field] = value;
      await api(`/cleaning-jobs/${jobId}`, {
        method: 'PATCH',
        body: JSON.stringify(payload)
      });
      await safeReload?.();
      await loadJobs(); // Reload jobs to reflect changes
      if (selectedJob?.id === jobId) {
        setSelectedJob(prev => ({...prev, [field]: value}));
      }
    } catch (err) {
      console.error('Failed to update job field:', err);
    }
  }

  // ===== ADD MATERIAL =====
  function addMaterial(jobId, currentMaterials, onUpdate) {
    const name = window.prompt('Nazwa materiału (np. papier, mydło):');
    if (!name) return;
    const amount = window.prompt('Ilość (np. 2 rolki):');
    if (!amount) return;
    let mats = [];
    try { mats = JSON.parse(currentMaterials || '[]'); } catch {}
    const updated = [...mats, { name, amount }];
    onUpdate(jobId, 'materials', JSON.stringify(updated));
  }

  // ===== ISSUE SECTION COMPONENT =====
  function IssueSection({ jobId, user }) {
    const [issues, setIssues] = useState([]);
    const [newDesc, setNewDesc] = useState('');
    const [showForm, setShowForm] = useState(false);

    useEffect(() => {
      if (jobId) loadIssues();
    }, [jobId]);

    async function loadIssues() {
      try { setIssues(await api(`/cleaning-jobs/${jobId}/issues`)); } catch {}
    }

    async function addIssue(e) {
      e.preventDefault();
      if (!newDesc.trim()) return;
      await api(`/cleaning-jobs/${jobId}/issues`, {
        method: 'POST',
        body: JSON.stringify({ description: newDesc.trim() })
      });
      setNewDesc('');
      setShowForm(false);
      await loadIssues();
    }

    async function resolveIssue(issueId) {
      await api(`/issues/${issueId}/resolve`, { method: 'PATCH' });
      await loadIssues();
    }

    return (
      <div className="issues-section">
        {issues.length === 0 && !showForm && <p className="hint">Brak zgłoszonych problemów.</p>}
        {issues.map(issue => (
          <div key={issue.id} className={`issue-item ${issue.resolved ? 'resolved' : ''}`}>
            <div className="issue-body">
              <strong><AlertTriangle size={13}/> {issue.description}</strong>
              <small>{issue.created_at}</small>
            </div>
            {!issue.resolved && safeUser.role === 'admin' && (
              <button className="btn-resolve" onClick={() => resolveIssue(issue.id)}>✓</button>
            )}
            {issue.resolved && <span className="resolved-badge">✓ Rozwiązane</span>}
          </div>
        ))}
        {showForm && (
          <form className="issue-form" onSubmit={addIssue}>
            <textarea value={newDesc} onChange={e => setNewDesc(e.target.value)} rows={2}
              placeholder="Opisz problem, np. brak papieru, uszkodzone lustro..." autoFocus />
            <div className="issue-form-actions">
              <button type="submit">Wyślij</button>
              <button type="button" className="btn-cancel" onClick={() => setShowForm(false)}><X size={14}/></button>
            </div>
          </form>
        )}
        {!showForm && (
          <button className="btn-report" onClick={() => setShowForm(true)}>
            <AlertTriangle size={14}/> Zgłoś problem
          </button>
        )}
        <style>{`
          .issues-section { margin-top: 8px; }
          .issue-item { display: flex; align-items: flex-start; gap: 8px; padding: 10px; background: #fef9c3; border-radius: 8px; margin-bottom: 8px; border: 1px solid #fcd34d; }
          .issue-item.resolved { background: #f0fdf4; border-color: #86efac; opacity: 0.7; }
          .issue-body { flex: 1; }
          .issue-body strong { display: flex; align-items: center; gap: 6px; font-size: 13px; }
          .issue-body small { display: block; color: #92400e; font-size: 11px; margin-top: 2px; }
          .resolved-badge { font-size: 11px; color: #166534; font-weight: 600; }
          .btn-resolve { background: #16a34a; color: white; border: none; border-radius: 6px; padding: 4px 10px; cursor: pointer; font-size: 12px; min-height: 30px; }
          .btn-report { background: #dc2626; color: white; border: none; border-radius: 8px; padding: 10px 14px; cursor: pointer; display: flex; align-items: center; gap: 6px; font-size: 13px; min-height: 44px; width: 100%; justify-content: center; }
          .issue-form { background: #fffbeb; border: 1px solid #fcd34d; border-radius: 8px; padding: 12px; margin-top: 8px; }
          .issue-form textarea { margin: 0 0 8px; min-height: 80px; font-size: 14px; }
          .issue-form-actions { display: flex; gap: 8px; }
          .issue-form-actions button { min-height: 40px; }
          .btn-cancel { background: #94a3b8; }
        `}</style>
      </div>
    );
  }

  // ===== PHOTO UPLOAD COMPONENT =====
  function PhotoUpload({ jobId, existingPhotos, onUploaded }) {
    const [photos, setPhotos] = useState(existingPhotos || []);
    const [saving, setSaving] = useState(false);

    async function saveToBackend(updatedPhotos) {
      setSaving(true);
      try {
        await api(`/cleaning-jobs/${jobId}`, {
          method: 'PATCH',
          body: JSON.stringify({ photos: updatedPhotos })
        });
        setPhotos(updatedPhotos);
        onUploaded();
      } catch (err) {
        console.error('Failed to save photos:', err);
      } finally {
        setSaving(false);
      }
    }

    function handleFiles(files) {
      const newPhotos = [];
      let processed = 0;
      Array.from(files).forEach(file => {
        if (!file.type.startsWith('image/')) return;
        const reader = new FileReader();
        reader.onload = (e) => {
          newPhotos.push(e.target.result);
          processed++;
          if (processed === Array.from(files).filter(f => f.type.startsWith('image/')).length) {
            const updated = [...photos, ...newPhotos];
            saveToBackend(updated);
          }
        };
        reader.readAsDataURL(file);
      });
    }

    function removePhoto(idx) {
      const updated = photos.filter((_, i) => i !== idx);
      saveToBackend(updated);
    }

    return (
      <div className="photo-upload">
        {/* Existing photos */}
        {photos.length > 0 && (
          <div className="photo-grid">
            {photos.map((src, idx) => (
              <div key={idx} className="photo-thumb">
                <img src={src} alt={`Zdjęcie ${idx + 1}`} onClick={() => window.open(src, '_blank')} />
                <button className="photo-remove" onClick={() => removePhoto(idx)}>×</button>
              </div>
            ))}
          </div>
        )}

        {/* Add more photos */}
        <label className="photo-add-btn">
          <input type="file" accept="image/*" multiple style={{display:'none'}} onChange={e => handleFiles(e.target.files)} />
          <span>+ Dodaj zdjęcie</span>
        </label>

        {saving && <small>Zapisywanie...</small>}

        <style>{`
          .photo-upload { margin-top: 12px; }
          .photo-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; margin-bottom: 10px; }
          .photo-thumb { position: relative; aspect-ratio: 1; border-radius: 12px; overflow: hidden; background: #f1f5f9; box-shadow: 0 2px 6px rgba(0,0,0,0.08); }
          .photo-thumb img { width: 100%; height: 100%; object-fit: cover; cursor: pointer; border-radius: 12px; }
          .photo-remove { position: absolute; top: 2px; right: 2px; width: 22px; height: 22px; background: rgba(0,0,0,0.7); color: white; border: none; border-radius: 50%; cursor: pointer; font-size: 14px; line-height: 1; display: flex; align-items: center; justify-content: center; }
          .photo-add-btn { display: flex; align-items: center; justify-content: center; padding: 14px; border: 2px dashed #cbd5e1; border-radius: 10px; cursor: pointer; color: #64748b; font-size: 14px; font-weight: 600; }
          .photo-add-btn:hover { border-color: #4f46e5; color: #4f46e5; }
          .photo-add-btn span { display: flex; align-items: center; gap: 6px; }
        `}</style>
      </div>
    );
  }

  const safeJobs = jobs || [];
  const isStaffPanel = safeUser.role === 'staff';
  const openJobs = safeJobs.filter(j => j.status !== 'done').sort((a, b) => `${a.scheduled_date}-${a.id}`.localeCompare(`${b.scheduled_date}-${b.id}`));
  const primaryJob = selectedJob || openJobs.find(j => j.scheduled_date === today) || openJobs[0] || null;
  const todayCount = safeJobs.filter(j => j.scheduled_date === today && j.status !== 'done').length;
  const weekCount = safeJobs.filter(j => {
    const t = new Date(today);
    const end = new Date(t); end.setDate(end.getDate() + 7);
    return new Date(j.scheduled_date) >= t && new Date(j.scheduled_date) <= end && j.status !== 'done';
  }).length;
  const allCount = safeJobs.filter(j => j.status !== 'done').length;

  useEffect(() => {
    if (!isStaffPanel || selectedJob || !primaryJob) return;
    openJob(primaryJob);
  }, [isStaffPanel, selectedJob, primaryJob?.id]);

  function statusLabel(job) {
    if (!job) return '';
    return job.status_label || (job.status === 'planned' ? 'Do zrobienia' : job.status === 'in_progress' ? 'W trakcie' : 'Zakończone');
  }

  function actionLabel(job) {
    if (!job) return 'Otwórz zadanie';
    return job.primary_action_label || (job.status === 'planned' ? 'Rozpocznij sprzątanie' : job.status === 'in_progress' ? 'Zakończ sprzątanie' : 'Sprzątanie zakończone');
  }

  function peopleWord(count) {
    if (count === 1) return 'osoba';
    if (count >= 2 && count <= 4) return 'osoby';
    return 'osób';
  }

  function handlePrimaryAction(job) {
    if (!job) return;
    if (job.status === 'planned') return startJob(job);
    if (job.status === 'in_progress') return markDone(job);
    return openJob(job);
  }

  if (loading) {
    return <div className="loading">Ładowanie zadań sprzątania...</div>;
  }

  if (isStaffPanel) {
    return <section className="maria-work-panel">
      <PageHeader
        icon="🧹"
        title="Mario, Twoje sprzątanie"
        description="Najważniejsze informacje są tutaj. Kliknij duży przycisk i idź krok po kroku."
        stats={[
          { label: 'Dziś', value: todayCount, tone: 'info' },
          { label: 'Do zrobienia', value: allCount, tone: allCount ? 'warning' : 'success' },
        ]}
      />

      {!primaryJob && (
        <div className="maria-empty-card">
          <CheckCircle size={34}/>
          <h2>Nie masz teraz sprzątania</h2>
          <p>Gdy pojawi się nowe zadanie, zobaczysz je tutaj jako pierwsze.</p>
        </div>
      )}

      {primaryJob && (
        <div className="maria-grid">
          <div className="maria-main-card">
            <div className="maria-status-row">
              <span className={`maria-status ${primaryJob.status}`}>{statusLabel(primaryJob)}</span>
              <span className="maria-date">{formatDate(primaryJob.scheduled_date)}</span>
            </div>
            <h2>Sprzątanie po gościu</h2>
            <p className="maria-guest-name">{primaryJob.guest_name || 'Poprzedni gość'}</p>
            <p className="maria-small">Wyjazd: {primaryJob.checkout || primaryJob.scheduled_date}</p>

            <div className="maria-next-box">
              <h3>Przygotuj dla następnych gości</h3>
              {primaryJob.prepare_guest_count > 0 ? (
                <>
                  <p className="maria-next-name">{primaryJob.prepare_guest_name}</p>
                  <div className="maria-big-number">{primaryJob.prepare_guest_count} {peopleWord(primaryJob.prepare_guest_count)}</div>
                  <ul>
                    <li>Pościel: {primaryJob.prepare_linen_count} kompletów</li>
                    <li>Ręczniki: {primaryJob.prepare_towel_count} szt.</li>
                    <li>{primaryJob.prepare_sleeping_note}</li>
                  </ul>
                  <p className="maria-small">Przyjazd: {primaryJob.prepare_checkin}</p>
                </>
              ) : (
                <p>{primaryJob.prepare_label || 'Brak kolejnej rezerwacji.'}</p>
              )}
            </div>

            {primaryJob.guest_notes && (
              <div className="maria-note-box">
                <strong>Ważna uwaga</strong>
                <p>{primaryJob.guest_notes}</p>
              </div>
            )}

            {primaryJob.status !== 'done' && (
              <button className="maria-primary-action" onClick={() => handlePrimaryAction(primaryJob)}>
                {primaryJob.status === 'planned' ? <Clock size={20}/> : <CheckCircle size={20}/>} {actionLabel(primaryJob)}
              </button>
            )}
            {primaryJob.status === 'done' && <div className="maria-done-message">✓ Sprzątanie zakończone</div>}
          </div>

          <div className="maria-steps-card">
            <h2>Lista kroków</h2>
            {checklist.length === 0 && <p className="hint">Ładowanie checklisty...</p>}
            <div className="maria-checklist">
              {checklist.map(i => (
                <label key={i.id} className="maria-check">
                  <input type="checkbox" checked={!!i.completed} onChange={() => toggle(i)} disabled={primaryJob.status === 'done'}/>
                  <span>{safeUser.language === 'uk' ? i.label_uk : i.label_pl}</span>
                </label>
              ))}
            </div>

            <hr/>
            <h2>Problem?</h2>
            <p className="maria-small">Jeśli czegoś brakuje albo coś jest uszkodzone, zgłoś tutaj.</p>
            <IssueSection jobId={primaryJob.id} user={safeUser} />

            <hr/>
            <h2>Zdjęcia</h2>
            {primaryJob.status === 'done' ? (
              <PhotoUpload jobId={primaryJob.id} existingPhotos={primaryJob.photos ? JSON.parse(primaryJob.photos) : []} onUploaded={() => { load(); }} />
            ) : (
              <p className="hint">Zdjęcia dodaj po zakończeniu sprzątania.</p>
            )}
          </div>

          {openJobs.length > 1 && (
            <div className="maria-other-card">
              <h2>Później</h2>
              {openJobs.filter(j => j.id !== primaryJob.id).slice(0, 5).map(j => (
                <button key={j.id} className="maria-other-job" onClick={() => openJob(j)}>
                  <span>{formatDate(j.scheduled_date)}</span>
                  <strong>{j.guest_name}</strong>
                  <small>{statusLabel(j)}</small>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <style>{`
        .maria-work-panel { max-width: 1120px; margin: 0 auto; }
        .maria-grid { display: grid; grid-template-columns: minmax(280px, 0.95fr) minmax(320px, 1.05fr); gap: 18px; align-items: start; }
        .maria-main-card, .maria-steps-card, .maria-other-card, .maria-empty-card { background: white; border: 1px solid #e2e8f0; border-radius: 18px; padding: 20px; box-shadow: 0 8px 24px rgba(15,23,42,0.06); }
        .maria-empty-card { text-align: center; padding: 36px; color: #166534; }
        .maria-status-row { display: flex; justify-content: space-between; gap: 10px; align-items: center; margin-bottom: 14px; }
        .maria-status { border-radius: 999px; padding: 7px 12px; font-weight: 800; font-size: 13px; background: #fef3c7; color: #92400e; }
        .maria-status.in_progress { background: #dbeafe; color: #1d4ed8; }
        .maria-status.done { background: #dcfce7; color: #166534; }
        .maria-date, .maria-small { color: #64748b; font-size: 14px; }
        .maria-guest-name { font-size: 24px; font-weight: 800; margin: 4px 0; color: #0f172a; }
        .maria-next-box { margin-top: 18px; padding: 18px; border-radius: 16px; background: #fff7ed; border: 1px solid #fed7aa; color: #7c2d12; }
        .maria-next-box h3 { margin-top: 0; }
        .maria-next-name { font-weight: 800; font-size: 18px; margin-bottom: 6px; }
        .maria-big-number { font-size: 34px; font-weight: 900; color: #9a3412; margin: 8px 0; }
        .maria-next-box ul { margin: 10px 0 0; padding-left: 20px; line-height: 1.8; font-size: 16px; }
        .maria-note-box { margin-top: 14px; padding: 14px; border-radius: 14px; background: #fef2f2; border: 1px solid #fecaca; color: #991b1b; }
        .maria-primary-action { width: 100%; min-height: 58px; margin-top: 18px; border: none; border-radius: 16px; background: #16a34a; color: white; font-size: 18px; font-weight: 900; display: flex; align-items: center; justify-content: center; gap: 10px; cursor: pointer; }
        .maria-primary-action:hover { background: #15803d; }
        .maria-done-message { margin-top: 18px; padding: 16px; border-radius: 14px; background: #dcfce7; color: #166534; font-weight: 800; text-align: center; }
        .maria-checklist { display: grid; gap: 10px; }
        .maria-check { display: flex; align-items: flex-start; gap: 12px; padding: 13px; border: 1px solid #e2e8f0; border-radius: 14px; background: #f8fafc; font-size: 15px; line-height: 1.35; }
        .maria-check input { width: 22px; height: 22px; flex: 0 0 auto; }
        .maria-other-card { grid-column: 1 / -1; }
        .maria-other-job { width: 100%; border: 1px solid #e2e8f0; background: #f8fafc; border-radius: 12px; padding: 12px; margin-top: 8px; display: grid; gap: 2px; text-align: left; cursor: pointer; }
        @media (max-width: 860px) { .maria-grid { grid-template-columns: 1fr; } }
      `}</style>
    </section>;
  }

  const currentActionJob = safeJobs.find(j => j.is_current_action) || openJobs.find(j => j.status === 'in_progress') || openJobs[0] || null;
  const currentActionIsOverdue = Boolean(currentActionJob && currentActionJob.is_alarm);
  const doneCount = safeJobs.filter(j => j.status === 'done').length;
  const inProgressCount = safeJobs.filter(j => j.status === 'in_progress').length;
  const plannedCount = safeJobs.filter(j => j.status === 'planned').length;
  const overdueCount = safeJobs.filter(j => j.status !== 'done' && j.scheduled_date < today).length;
  const consumedReadinessCount = safeJobs.filter(j => j.is_readiness_consumed).length;
  const recleaningCount = safeJobs.filter(j => j.is_recleaning_reopened || j.requires_recleaning_reason || j.invalidated_reason).length;
  const selectedChecklistDone = checklist.filter(i => i.completed).length;
  const selectedChecklistTotal = checklist.length;
  const selectedProgress = selectedChecklistTotal ? Math.round((selectedChecklistDone / selectedChecklistTotal) * 100) : 0;

  function statusTone(job) {
    if (job.status === 'done' && job.is_readiness_consumed) return 'consumed';
    if (job.is_recleaning_reopened || job.requires_recleaning_reason || job.invalidated_reason) return 'reclean';
    if (job.status === 'done') return 'done';
    if (job.status === 'in_progress') return 'in_progress';
    if (job.is_current_action) return 'current';
    if (job.scheduled_date < today) return 'late';
    return 'planned';
  }

  function adminStatusLabel(job) {
    if (job.status === 'done' && job.is_readiness_consumed) return 'Gotowość zużyta';
    if (job.is_recleaning_reopened || job.requires_recleaning_reason || job.invalidated_reason) return 'Ponowne sprzątanie';
    if (job.status === 'done') return 'Zakończone';
    if (job.status === 'in_progress') return 'W trakcie';
    if (job.is_current_action) return 'Do sprzątania teraz';
    if (job.scheduled_date < today) return 'Zaległe';
    return 'Zaplanowane';
  }

  function cleaningTimelineText(job) {
    if (!job) return '';
    if (job.timeline_status_label) return job.timeline_status_label;
    if (job.is_readiness_consumed) {
      return `Gotowość mieszkania została zużyta${job.consumed_by_reservation_number ? ` przez rezerwację ${job.consumed_by_reservation_number}` : ''}.`;
    }
    if (job.is_recleaning_reopened || job.requires_recleaning_reason || job.invalidated_reason) {
      return 'Po pobycie pojawiła się nowa rezerwacja — wymagane jest kolejne sprzątanie przed następnym check-in.';
    }
    return '';
  }

  function parseMaterials(value) {
    try {
      const parsed = JSON.parse(value || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  const normalizedSearch = adminSearch.trim().toLowerCase();
  const alarmJobs = safeJobs
    .filter(j => j.status !== 'done' && j.is_alarm)
    .filter(j => {
      if (!normalizedSearch) return true;
      return [j.guest_name, j.reservation_number, j.prepare_guest_name, j.prepare_reservation_number, j.scheduled_date, j.alarm_label, j.timeline_status_label, j.consumed_by_reservation_number, j.consumed_by_guest_name]
        .filter(Boolean)
        .some(v => String(v).toLowerCase().includes(normalizedSearch));
    })
    .sort((a, b) => String(a.scheduled_date).localeCompare(String(b.scheduled_date)) || Number(a.id || 0) - Number(b.id || 0));
  const adminFilteredJobs = safeJobs
    .filter(j => {
      if (filter === 'action') return j.status === 'in_progress' || (j.is_current_action && !j.is_alarm) || j.is_recleaning_reopened || j.requires_recleaning_reason || j.invalidated_reason;
      if (filter === 'upcoming') return j.status !== 'done' && !j.is_alarm;
      if (filter === 'planned') return j.status === 'planned' && !j.is_alarm;
      if (filter === 'done') return j.status === 'done';
      return true;
    })
    .filter(j => {
      if (!normalizedSearch) return true;
      return [j.guest_name, j.reservation_number, j.prepare_guest_name, j.prepare_reservation_number, j.scheduled_date, j.timeline_status_label, j.consumed_by_reservation_number, j.consumed_by_guest_name]
        .filter(Boolean)
        .some(v => String(v).toLowerCase().includes(normalizedSearch));
    })
    .sort((a, b) => {
      if (filter === 'done') return `${b.scheduled_date}-${b.id}`.localeCompare(`${a.scheduled_date}-${a.id}`);
      const priority = job => job.status === 'in_progress' ? 0 : job.is_current_action ? 1 : job.scheduled_date < today ? 2 : 3;
      return priority(a) - priority(b) || `${a.scheduled_date}-${a.id}`.localeCompare(`${b.scheduled_date}-${b.id}`);
    });

  return <section className="admin-cleaning-page">
    <PageHeader
      icon="🧹"
      title="Sprzątanie — centrum operacyjne"
      description="Widok admina: alarmy sprzątania, sprzątania w toku i przyszłe terminy — bez backlogu po terminie."
      helpKey="cleaning-admin"
      stats={[
        { label: 'Do reakcji', value: alarmJobs.length + inProgressCount + recleaningCount, tone: (alarmJobs.length + inProgressCount + recleaningCount) ? 'warning' : 'success' },
        { label: 'W trakcie', value: inProgressCount, tone: inProgressCount ? 'primary' : 'info' },
        { label: 'Gotowość zużyta', value: consumedReadinessCount, tone: consumedReadinessCount ? 'info' : 'success' },
        { label: 'Ponowne', value: recleaningCount, tone: recleaningCount ? 'warning' : 'success' },
        { label: 'Archiwum', value: doneCount, tone: 'success' },
      ]}
    />

    {alarmJobs.length > 0 && (
      <article className="admin-cleaning-hero danger">
        <div>
          <span className="eyebrow">ALARM</span>
          <h2>Brak gotowości mieszkania</h2>
          <p>Maria nie domknęła sprzątania na czas. To nie jest backlog — to incydent do natychmiastowej reakcji.</p>
          <p className="hero-prep">{alarmJobs.length} zaległych zadań wymagają potwierdzenia lub zakończenia zanim pojawi się kolejny gość.</p>
        </div>
        <button className="primary" onClick={() => openJob(alarmJobs[0])}>Otwórz alarm</button>
      </article>
    )}

    {currentActionJob && (
        <article className={`admin-cleaning-hero ${statusTone(currentActionJob)}`}>
        <div>
          <span className="eyebrow">{currentActionIsOverdue ? 'Zaległe do reakcji' : 'Najważniejsze teraz'}</span>
          <h2>{currentActionIsOverdue ? 'Zaległe sprzątanie' : adminStatusLabel(currentActionJob)} · {formatDate(currentActionJob.scheduled_date)} {currentActionJob.checkout_time || '11:00'}</h2>
          <p>
            Po rezerwacji <strong>{currentActionJob.reservation_number || '—'}</strong>
            {currentActionJob.guest_name ? <> · {currentActionJob.guest_name}</> : null}
          </p>
          {currentActionIsOverdue ? (
            <p className="hero-prep">To backlog po terminie, nie przyszły slot. Trzeba zamknąć zaległość przed dalszym planowaniem.</p>
          ) : currentActionJob.prepare_guest_count > 0 && (
            <p className="hero-prep">Przygotować dla rezerwacji {currentActionJob.prepare_reservation_number}: {currentActionJob.prepare_guest_count} {peopleWord(currentActionJob.prepare_guest_count)}</p>
          )}
        </div>
        <button className="primary" onClick={() => openJob(currentActionJob)}>Otwórz zadanie</button>
      </article>
    )}

    <div className="admin-cleaning-toolbar">
      <div className="filter-tabs admin-cleaning-tabs">
        <button className={filter === 'action' ? 'active' : ''} onClick={() => setFilter('action')}>Do reakcji <span className="badge">{overdueCount + inProgressCount + recleaningCount}</span></button>
        <button className={filter === 'upcoming' ? 'active' : ''} onClick={() => setFilter('upcoming')}>Najbliższe <span className="badge">{allCount}</span></button>
        <button className={filter === 'planned' ? 'active' : ''} onClick={() => setFilter('planned')}>Zaplanowane <span className="badge">{plannedCount}</span></button>
        <button className={filter === 'done' ? 'active' : ''} onClick={() => setFilter('done')}>Archiwum <span className="badge">{doneCount}</span></button>
        <button className={filter === 'all' ? 'active' : ''} onClick={() => setFilter('all')}>Wszystkie <span className="badge">{safeJobs.length}</span></button>
      </div>
      <input
        className="admin-cleaning-search"
        value={adminSearch}
        onChange={e => setAdminSearch(e.target.value)}
        placeholder="Szukaj: gość, numer rezerwacji, data..."
      />
    </div>

    <div className="admin-cleaning-layout">
      <article className="card admin-cleaning-queue">
        <div className="section-title-row">
          <div>
            <h2>Najbliższe sprzątania</h2>
            <p className="hint">{adminFilteredJobs.length} zadań w tym widoku</p>
          </div>
        </div>
        {adminFilteredJobs.length === 0 && <p className="hint">Brak zadań dla wybranego filtra.</p>}
        <div className="admin-job-list">
          {adminFilteredJobs.map(j => {
            const isSelected = selectedJob?.id === j.id;
            return (
              <button key={j.id} className={`admin-job-card ${statusTone(j)} ${isSelected ? 'selected' : ''}`} onClick={() => openJob(j)}>
                <span className={`admin-status-dot ${statusTone(j)}`}></span>
                <div className="admin-job-main">
                  <div className="admin-job-top">
                    <strong>{formatDate(j.scheduled_date)} · {j.checkout_time || '11:00'}</strong>
                    <span className={`admin-status-pill ${statusTone(j)}`}>{adminStatusLabel(j)}</span>
                  </div>
                  <div className="admin-job-guest">{j.guest_name || 'Gość'} · rez. {j.reservation_number || '—'}</div>
                  <div className="admin-job-prep">
                    {j.prepare_guest_count > 0
                      ? <>Przygotuj dla {j.prepare_guest_count} {peopleWord(j.prepare_guest_count)} · rez. {j.prepare_reservation_number || '—'}</>
                      : 'Brak kolejnej rezerwacji / brak danych przygotowania'}
                  </div>
                  {j.availability_label && <small>{j.availability_label}</small>}
                  {cleaningTimelineText(j) && <small className="admin-timeline-note">{cleaningTimelineText(j)}</small>}
                </div>
              </button>
            );
          })}
        </div>
      </article>

      <article className="card admin-cleaning-detail">
        {!selectedJob && (
          <div className="empty-detail">
            <Clock size={34}/>
            <h2>Wybierz zadanie</h2>
            <p>Po lewej wybierz sprzątanie, żeby zobaczyć checklistę, przygotowanie, notatki i akcje admina.</p>
          </div>
        )}
        {selectedJob && (
          <div>
            <div className="admin-detail-head">
              <div>
                <span className={`admin-status-pill ${statusTone(selectedJob)}`}>{adminStatusLabel(selectedJob)}</span>
                <h2>{selectedJob.guest_name || 'Sprzątanie'} </h2>
                <p className="muted">Rezerwacja {selectedJob.reservation_number || '—'} · {selectedJob.scheduled_date} · checkout {selectedJob.checkout_time || '11:00'}</p>
                {cleaningTimelineText(selectedJob) && (
                  <p className={`admin-timeline-banner ${statusTone(selectedJob)}`}>
                    {cleaningTimelineText(selectedJob)}
                  </p>
                )}
              </div>
              <div className="admin-detail-actions">
                <button className="secondary" onClick={() => {
                  navigator.clipboard.writeText(`${window.location.origin}/sprzatanie/${selectedJob.id}`);
                  const toast = document.createElement('div');
                  toast.innerText = 'Skopiowano link do sprzątania';
                  toast.className = 'toast';
                  document.body.appendChild(toast);
                  setTimeout(() => document.body.removeChild(toast), 2000);
                }}>🔗 Link</button>
                {selectedJob.status === 'planned' && <button className="primary" onClick={() => startJob(selectedJob)}><Clock size={16}/> Rozpocznij</button>}
                {selectedJob.status === 'in_progress' && <button className="primary" onClick={() => markDone(selectedJob)}><CheckCircle size={16}/> Zakończ</button>}
                {selectedJob.status === 'done' && <span className="done-badge">✓ Zakończone</span>}
              </div>
            </div>

            <div className="admin-info-grid">
              <div className="admin-info-card prep">
                <span className="eyebrow">Przygotowanie</span>
                {selectedJob.prepare_guest_count > 0 ? (
                  <>
                    <strong>{selectedJob.prepare_guest_count} {peopleWord(selectedJob.prepare_guest_count)}</strong>
                    <span>Rez. {selectedJob.prepare_reservation_number || '—'} · przyjazd {selectedJob.prepare_checkin || '—'}</span>
                    <small>Pościel: {selectedJob.prepare_linen_count || 0} · ręczniki: {selectedJob.prepare_towel_count || 0}</small>
                    {selectedJob.prepare_guest_notes && <small className="warning-text">{selectedJob.prepare_guest_notes}</small>}
                  </>
                ) : <span>{selectedJob.prepare_label || 'Brak kolejnej rezerwacji.'}</span>}
              </div>
              <div className="admin-info-card progress">
                <span className="eyebrow">Checklista</span>
                <strong>{selectedChecklistDone}/{selectedChecklistTotal || 0}</strong>
                <div className="progress-bar"><span style={{width: `${selectedProgress}%`}}></span></div>
                <small>{selectedProgress}% wykonane</small>
              </div>
              <div className="admin-info-card timeline">
                <span className="eyebrow">Logika gotowości</span>
                {selectedJob.is_readiness_consumed ? (
                  <>
                    <strong>Zużyta</strong>
                    <span>Przez rez. {selectedJob.consumed_by_reservation_number || selectedJob.consumed_by_booking_id || '—'}</span>
                    {selectedJob.consumed_by_guest_name && <small>{selectedJob.consumed_by_guest_name}</small>}
                  </>
                ) : (selectedJob.is_recleaning_reopened || selectedJob.requires_recleaning_reason || selectedJob.invalidated_reason) ? (
                  <>
                    <strong>Ponowne</strong>
                    <span>Wymagane po zmianie kolejności pobytów</span>
                    {(selectedJob.requires_recleaning_reason || selectedJob.invalidated_reason) && <small>{selectedJob.requires_recleaning_reason || selectedJob.invalidated_reason}</small>}
                  </>
                ) : (
                  <>
                    <strong>Aktywna</strong>
                    <span>Sprzątanie przygotowuje mieszkanie do tej rezerwacji.</span>
                  </>
                )}
              </div>
            </div>

            <div className="admin-detail-grid">
              <section>
                <h3>Checklista</h3>
                <div className="checklist admin-checklist">
                  {checklist.map(i => (
                    <label key={i.id} className="check">
                      <input type="checkbox" checked={!!i.completed} onChange={() => toggle(i)} disabled={selectedJob.status === 'done'}/>
                      <span>{safeUser.language === 'uk' ? i.label_uk : i.label_pl}</span>
                    </label>
                  ))}
                  {checklist.length === 0 && <p className="hint">Ładowanie checklisty...</p>}
                </div>

                <h3>Problemy</h3>
                <IssueSection jobId={selectedJob.id} user={safeUser} />
              </section>

              <aside className="admin-side-panel">
                <h3>Informacje dla sprzątania</h3>
                <label className="field-label">Liczba osób / poprzedni pobyt</label>
                <input type="number" min="0" max="20" value={selectedJob.guest_count || 0}
                  onChange={e => updateJobField(selectedJob.id, 'guest_count', parseInt(e.target.value) || 0)}
                  disabled={selectedJob.status === 'done'} />

                <label className="field-label">Notatki dla Marii / admina</label>
                <textarea value={selectedJob.guest_notes || ''} rows={3}
                  onChange={e => updateJobField(selectedJob.id, 'guest_notes', e.target.value)}
                  disabled={selectedJob.status === 'done'}
                  placeholder="np. późny przyjazd, dodatkowe ręczniki, uwagi gościa..." />

                <label className="field-label">Godziny pracy</label>
                <input type="number" step="0.5" min="0" max="24" value={selectedJob.hours_worked || 0}
                  onChange={e => updateJobField(selectedJob.id, 'hours_worked', parseFloat(e.target.value) || 0)}
                  disabled={selectedJob.status === 'done'} />

                <div className="materials-list admin-materials">
                  <strong>Zużyte materiały</strong>
                  {parseMaterials(selectedJob.materials).length > 0
                    ? parseMaterials(selectedJob.materials).map((m, i) => <span key={i} className="material-tag">{m.name}: {m.amount}</span>)
                    : <span className="hint">brak</span>}
                </div>
                {selectedJob.status !== 'done' && (
                  <button className="secondary" onClick={() => addMaterial(selectedJob.id, selectedJob.materials, updateJobField)}>
                    <Plus size={14}/> Dodaj materiał
                  </button>
                )}

                {selectedJob.confirmed_by_cleaner_at && (
                  <div className="cleaner-confirmation-box">
                    <strong>✓ Potwierdzone przez Marię</strong>
                    <span>{formatDateTime(selectedJob.confirmed_by_cleaner_at)}</span>
                    {selectedJob.confirmation_message_text && <p>{selectedJob.confirmation_message_text}</p>}
                    {selectedJob.confirmation_message_id && <small>WhatsApp: {selectedJob.confirmation_message_id}</small>}
                  </div>
                )}
                {selectedJob.proposed_confirmation_at && !selectedJob.confirmed_by_cleaner_at && (
                  <div className="cleaner-confirmation-box proposed">
                    <strong>Proponowane: potwierdzone przez Marię</strong>
                    <span>{formatDateTime(selectedJob.proposed_confirmation_at)}</span>
                    {selectedJob.proposed_confirmation_message_text && <p>{selectedJob.proposed_confirmation_message_text}</p>}
                    {selectedJob.proposed_confirmation_message_id && <small>WhatsApp: {selectedJob.proposed_confirmation_message_id}</small>}
                  </div>
                )}

                <h3>Rozliczenie z Marią</h3>
                <div className="payment-summary">
                  <div className="payment-row">
                    <span>Kwota:</span>
                    <strong>{selectedJob.payment_amount || 100} PLN</strong>
                  </div>
                  <div className="payment-row">
                    <span>Status:</span>
                    {selectedJob.payment_status === 'paid' ? (
                      <span className="payment-pill paid">✓ Zapłacone</span>
                    ) : selectedJob.payment_status === 'historical_no_payment' ? (
                      <span className="payment-pill neutral">Historyczne — bez rozliczenia</span>
                    ) : (
                      <span className="payment-pill unpaid">Niezapłacone</span>
                    )}
                  </div>
                  {selectedJob.payment_status === 'paid' && (selectedJob.payment_paid_at || selectedJob.payment_paid_by_name) && (
                    <div className="payment-meta">
                      {selectedJob.payment_paid_by_name && <span>przez {selectedJob.payment_paid_by_name}</span>}
                      {selectedJob.payment_paid_at && <span> · {formatDateTime(selectedJob.payment_paid_at)}</span>}
                    </div>
                  )}
                </div>
                {selectedJob.status === 'done' && !['paid','historical_no_payment'].includes(selectedJob.payment_status) && (
                  <button className="primary payment-confirm-btn" onClick={() => confirmPayment(selectedJob)}>
                    <CheckCircle size={16}/> Potwierdź zapłatę ({selectedJob.payment_amount || 100} PLN)
                  </button>
                )}
                {selectedJob.status !== 'done' && (
                  <p className="hint">Płatność można potwierdzić po zakończeniu sprzątania.</p>
                )}

                <h3>Zdjęcia po sprzątaniu</h3>
                {selectedJob.status === 'done'
                  ? <PhotoUpload jobId={selectedJob.id} existingPhotos={selectedJob.photos ? JSON.parse(selectedJob.photos) : []} onUploaded={() => { load(); }} />
                  : <p className="hint">Zdjęcia pojawią się po zakończeniu zadania.</p>}
              </aside>
            </div>
          </div>
        )}
      </article>
    </div>

    <style>{`
      .admin-cleaning-page { max-width: 1380px; margin: 0 auto; }
      .admin-cleaning-hero { display: flex; justify-content: space-between; align-items: center; gap: 18px; padding: 20px; margin-bottom: 16px; border-radius: 18px; border: 1px solid #bfdbfe; background: linear-gradient(135deg, #eff6ff, #f8fafc); box-shadow: 0 10px 26px rgba(15,23,42,0.06); }
      .admin-cleaning-hero h2 { margin: 4px 0; font-size: 22px; }
      .admin-cleaning-hero p { margin: 4px 0; color: #475569; }
      .admin-cleaning-hero.in_progress, .admin-cleaning-hero.current { border-color: #93c5fd; background: linear-gradient(135deg, #dbeafe, #f8fafc); }
      .admin-cleaning-hero.reclean { border-color: #fdba74; background: linear-gradient(135deg, #ffedd5, #fff7ed); }
      .admin-cleaning-hero.consumed { border-color: #c4b5fd; background: linear-gradient(135deg, #ede9fe, #f8fafc); }
      .admin-cleaning-hero.late { border-color: #fca5a5; background: linear-gradient(135deg, #fee2e2, #fff7ed); }
      .hero-prep { font-weight: 700; color: #9a3412 !important; }
      .eyebrow { display: block; font-size: 11px; text-transform: uppercase; letter-spacing: .08em; font-weight: 900; color: #64748b; }
      .admin-cleaning-toolbar { display: flex; justify-content: space-between; align-items: center; gap: 12px; margin-bottom: 16px; }
      .filter-tabs.admin-cleaning-tabs { flex-wrap: wrap; margin-bottom: 0; }
      .filter-tabs button { padding: 9px 14px; border: 1px solid #dbe3ef; background: white; border-radius: 999px; cursor: pointer; display: flex; align-items: center; gap: 6px; font-weight: 750; color: #334155; }
      .filter-tabs button.active { background: #4f46e5; color: white; border-color: #4f46e5; }
      .badge { background: rgba(15,23,42,0.09); padding: 2px 8px; border-radius: 10px; font-size: 12px; }
      .filter-tabs button.active .badge { background: rgba(255,255,255,0.25); }
      .admin-cleaning-search { max-width: 360px; min-width: 260px; border-radius: 12px; border: 1px solid #dbe3ef; padding: 11px 13px; }
      .admin-cleaning-layout { display: grid; grid-template-columns: minmax(330px, 0.82fr) minmax(520px, 1.18fr); gap: 18px; align-items: start; }
      .section-title-row { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; margin-bottom: 10px; }
      .section-title-row h2 { margin: 0; }
      .admin-job-list { display: grid; gap: 10px; max-height: calc(100vh - 330px); overflow: auto; padding-right: 4px; }
      .admin-job-card { width: 100%; display: grid; grid-template-columns: 10px 1fr; gap: 10px; text-align: left; background: #fff; border: 1px solid #e2e8f0; border-radius: 16px; padding: 12px; cursor: pointer; box-shadow: 0 3px 10px rgba(15,23,42,0.03); }
      .admin-job-card:hover, .admin-job-card.selected { border-color: #6366f1; background: #f8f7ff; }
      .admin-status-dot { width: 9px; height: 9px; border-radius: 99px; margin-top: 8px; background: #94a3b8; }
      .admin-status-dot.in_progress, .admin-status-dot.current { background: #2563eb; }
      .admin-status-dot.reclean { background: #ea580c; }
      .admin-status-dot.consumed { background: #7c3aed; }
      .admin-status-dot.late { background: #dc2626; }
      .admin-status-dot.done { background: #16a34a; }
      .admin-job-main { display: grid; gap: 4px; min-width: 0; }
      .admin-job-top { display: flex; justify-content: space-between; gap: 8px; align-items: center; }
      .admin-job-guest { color: #0f172a; font-weight: 700; }
      .admin-job-prep, .admin-job-card small { color: #64748b; font-size: 13px; }
      .admin-status-pill { border-radius: 999px; padding: 4px 8px; font-size: 11px; font-weight: 900; background: #f1f5f9; color: #475569; white-space: nowrap; }
      .admin-status-pill.in_progress, .admin-status-pill.current { background: #dbeafe; color: #1d4ed8; }
      .admin-status-pill.reclean { background: #ffedd5; color: #9a3412; }
      .admin-status-pill.consumed { background: #ede9fe; color: #5b21b6; }
      .admin-status-pill.late { background: #fee2e2; color: #991b1b; }
      .admin-status-pill.done { background: #dcfce7; color: #166534; }
      .admin-status-pill.planned { background: #f8fafc; color: #475569; }
      .empty-detail { text-align: center; padding: 46px 20px; color: #64748b; }
      .admin-detail-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 16px; border-bottom: 1px solid #e2e8f0; padding-bottom: 14px; margin-bottom: 16px; }
      .admin-detail-head h2 { margin: 8px 0 2px; font-size: 25px; }
      .admin-detail-actions { display: flex; flex-wrap: wrap; justify-content: flex-end; gap: 8px; }
      .admin-info-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; margin-bottom: 16px; }
      .admin-info-card { min-height: 110px; display: grid; gap: 5px; align-content: start; border-radius: 16px; border: 1px solid #e2e8f0; background: #f8fafc; padding: 14px; }
      .admin-info-card strong { font-size: 24px; color: #0f172a; }
      .admin-info-card.prep { background: #fff7ed; border-color: #fed7aa; color: #7c2d12; }
      .admin-info-card.progress { background: #eff6ff; border-color: #bfdbfe; }
      .admin-info-card.timeline { background: #f8fafc; border-color: #ddd6fe; color: #334155; }
      .admin-timeline-note { display: inline-block; margin-top: 3px; color: #7c2d12 !important; font-weight: 800; }
      .admin-timeline-banner { margin: 8px 0 0; padding: 10px 12px; border-radius: 12px; font-weight: 800; border: 1px solid #fed7aa; background: #fff7ed; color: #9a3412; }
      .admin-timeline-banner.consumed { border-color: #c4b5fd; background: #ede9fe; color: #5b21b6; }
      .admin-timeline-banner.done { border-color: #bbf7d0; background: #f0fdf4; color: #166534; }
      .progress-bar { height: 9px; border-radius: 999px; overflow: hidden; background: #dbeafe; }
      .progress-bar span { display: block; height: 100%; background: #2563eb; border-radius: inherit; }
      .admin-detail-grid { display: grid; grid-template-columns: minmax(280px, 1fr) minmax(260px, 0.78fr); gap: 18px; align-items: start; }
      .admin-checklist { display: grid; gap: 9px; margin-bottom: 18px; }
      .check { display: flex; align-items: center; gap: 10px; padding: 11px 12px; border: 1px solid #e2e8f0; border-radius: 12px; background: #f8fafc; }
      .check input { width: 20px; height: 20px; }
      .admin-side-panel { border-left: 1px solid #e2e8f0; padding-left: 18px; }
      .field-label { display: block; margin-top: 12px; margin-bottom: 6px; color: #475569; font-size: 13px; font-weight: 800; }
      .admin-side-panel input, .admin-side-panel textarea { width: 100%; border: 1px solid #dbe3ef; border-radius: 12px; padding: 10px 12px; }
      .admin-materials { display: flex; flex-wrap: wrap; gap: 7px; margin: 14px 0; }
      .material-tag { background: #eef2ff; color: #3730a3; border-radius: 999px; padding: 5px 9px; font-size: 12px; font-weight: 800; }
      .warning-text { color: #991b1b; font-weight: 800; }
      .done-badge { color: #166534; background: #dcfce7; padding: 9px 12px; border-radius: 999px; font-weight: 900; }
      .maria-occupancy-chip { padding: 8px 14px; border-radius: 999px; font-weight: 800; font-size: 13px; display: inline-block; }
      .maria-occupancy-chip.occupied { background: #ffedd5; color: #9a3412; border: 1px solid #fdba74; }
      .maria-occupancy-chip.empty { background: #dcfce7; color: #166534; border: 1px solid #86efac; }
      .toast { position: fixed; top: 20px; right: 20px; background: #16a34a; color: white; padding: 12px 20px; border-radius: 10px; z-index: 1000; box-shadow: 0 10px 24px rgba(0,0,0,.15); }
      @media (max-width: 1050px) { .admin-cleaning-layout, .admin-detail-grid { grid-template-columns: 1fr; } .admin-side-panel { border-left: 0; padding-left: 0; border-top: 1px solid #e2e8f0; padding-top: 16px; } .admin-job-list { max-height: none; } }
      @media (max-width: 760px) { .admin-cleaning-hero, .admin-cleaning-toolbar, .admin-detail-head { flex-direction: column; align-items: stretch; } .admin-cleaning-search { max-width: none; min-width: 0; } .admin-info-grid { grid-template-columns: 1fr; } }
      .payment-summary { display: grid; gap: 8px; padding: 12px; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; margin-bottom: 12px; }
      .payment-row { display: flex; justify-content: space-between; align-items: center; font-size: 14px; }
      .payment-row strong { font-size: 16px; color: #0f172a; }
      .payment-pill { font-size: 12px; font-weight: 700; padding: 4px 10px; border-radius: 999px; }
      .payment-pill.paid { background: #dcfce7; color: #166534; }
      .payment-pill.unpaid { background: #fef3c7; color: #92400e; }
      .payment-pill.neutral { background: #e2e8f0; color: #475569; }
      .payment-meta { font-size: 12px; color: #64748b; padding-top: 4px; border-top: 1px dashed #cbd5e1; }
      .payment-confirm-btn { width: 100%; margin-bottom: 12px; }
      .cleaner-confirmation-box { display: grid; gap: 4px; margin: 12px 0; padding: 12px; border-radius: 12px; border: 1px solid #86efac; background: #f0fdf4; color: #14532d; }
      .cleaner-confirmation-box strong { font-size: 14px; }
      .cleaner-confirmation-box span, .cleaner-confirmation-box small { color: #166534; font-size: 12px; }
      .cleaner-confirmation-box p { margin: 4px 0 0; color: #14532d; font-size: 13px; }
    `}</style>
  </section>;
}